import { hasScrollbar, ScrollableArea } from "@nova/component-scrollable-area"
import {
    ChangeEventHandler,
    FormEventHandler,
    Fragment,
    FunctionComponent,
    MouseEventHandler,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react"
import { GetMediaControls, MediaListener } from "./ColorFinderFootageDisplay"
import {
    BeenHereIcon,
    BrightnessAlertIcon,
    CommentIcon,
    DangerousIcon,
    NotListedIcon,
} from "./ColorFinderCommentStatuses"
import { Icon, ImageColorFinderFlight, VideoColorFinderFlight } from "@nova/protobuf"
import {
    useComments,
    useCreateComment,
    useDeleteComment,
    useUpdateComment,
} from "features/comments/Comments"
import * as Icons from "@nova/component-icons"
import { ButtonTertiary, IconButton } from "@nova/component-btn"
import { CFCommentStatus, Comment, CommentContextMap, CommentTarget } from "@nova/schemas"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { Divider } from "components/Divider"
import { selectColorFinderFeedFilters } from "./redux/selectors"
import classNames from "classnames"
import { useFullnameForUserId } from "features/data-management/users/storage"
import { getIconUrl } from "@nova/component-map-icons"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import { defaultIconColor } from "features/map/layers/icons/constants"
import { parseIconTargetPayload } from "plugins/IconTargetPayload"
import { Dropdown } from "@nova/component-dropdown"
import { showConfirmationModal } from "features/content-overlay/thunks"
import { WorkspaceConfirmationModalName } from "@nova/constants"
import { showGlobalModal } from "runlevel/actions"
import { ColorFinderTiler } from "./ColorFinderTiler.class"
import { isLocalColorFinderFlight, isVideoFlight } from "./ColorFinderQueries"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"

interface ColorFinderFeedProps {
    flight: ImageColorFinderFlight | VideoColorFinderFlight
    comments: Comment<"cf-frame">[]
    icons: Icon[]
    getMediaControls: GetMediaControls
    onCommentReplyClick: (comment: Comment<"cf-frame">) => void
    tiler: ColorFinderTiler
}

const ColorFinderFeed: FunctionComponent<ColorFinderFeedProps> = ({
    comments,
    onCommentReplyClick,
    icons,
    tiler,
    getMediaControls,
}) => {
    const [isScrollSynced, setIsScrollSynced] = useState(true)
    const { deleteComment } = useDeleteComment()
    const filters = useAppSelector(selectColorFinderFeedFilters)

    const [videoDuration, setVideoDuration] = useState(getMediaControls()?.getVideoDuration())
    const [isVideo, setIsVideo] = useState<boolean>()
    const [highlightedFrame, setHighlightedFrame] = useState(-1)

    comments = filters.comments ? comments : []
    icons = filters.points ? icons : []

    const sortedFeedItems = useMemo(() => {
        const commentItems = comments.map((c) => ({
            type: "comment" as const,
            data: c,
            frame: c.context.frame,
        }))

        const iconItems = icons.map((i) => ({
            type: "icon" as const,
            data: i,
            frame: parseIconTargetPayload(i.targetPayload)?.frame ?? 0,
        }))

        return [...commentItems, ...iconItems].sort((a, b) => a.frame - b.frame)
    }, [comments, icons])

    useEffect(() => {
        const controls = getMediaControls()

        const listener: MediaListener = (state) => {
            setVideoDuration(getMediaControls()?.getVideoDuration())
            setIsVideo(getMediaControls()?.type === "video")

            let low = 0
            let high = sortedFeedItems.length - 1
            let anchorIndex = -1

            while (low <= high) {
                const mid = (low + high) >>> 1
                if (sortedFeedItems[mid].frame <= state.frame) {
                    anchorIndex = mid
                    low = mid + 1
                } else {
                    high = mid - 1
                }
            }

            const newFrame = anchorIndex !== -1 ? sortedFeedItems[anchorIndex].frame : -1

            setHighlightedFrame(newFrame)
        }
        const currState = controls?.getState()
        if (currState) {
            listener(currState)
        }

        controls?.subscribe(listener)
        return () => {
            controls?.unsubscribe(listener)
        }
    }, [getMediaControls, sortedFeedItems])

    const getTimestamp = useCallback(
        (frame: number) => {
            if (isVideo === true) {
                if (!videoDuration || videoDuration === -1) return ""
                const numFrames = tiler.getNumFrames()
                const time = (frame / numFrames) * videoDuration
                return formatTimestamp(time)
            } else if (isVideo === false) {
                return frame + 1
            }
        },
        [isVideo, tiler, videoDuration]
    )

    const handleDelete = (comment: Comment) => deleteComment({ commentId: comment.id })

    return (
        <div className="relative h-full w-full">
            <ScrollableArea
                onWheel={(e) => hasScrollbar(e.currentTarget).vertical && setIsScrollSynced(false)}
                onTouchMove={(e) =>
                    hasScrollbar(e.currentTarget).vertical && setIsScrollSynced(false)
                }
                id="comment-container"
                className="relative h-full overflow-y-auto w-full"
            >
                <div className="flex flex-col justify-end absolute w-full py-2 gap-0.5">
                    {sortedFeedItems.map((item, i) => (
                        <div
                            key={i}
                            ref={(el) => {
                                if (item.frame === highlightedFrame && isScrollSynced) {
                                    el?.scrollIntoView({ block: "end" })
                                }
                            }}
                            className={classNames(
                                "w-full",
                                item.frame === highlightedFrame ? "bg-blue-700" : "cursor-pointer"
                            )}
                        >
                            {item.type === "icon" ? (
                                <ColorFinderFeedIcon
                                    icon={item.data}
                                    onIconClick={() => {
                                        const payload = parseIconTargetPayload(
                                            item.data.targetPayload
                                        )
                                        if (payload) getMediaControls()?.setFrame(payload.frame)
                                    }}
                                    timestamp={
                                        <span className={statusToTextColorClass("default")}>
                                            {getTimestamp(item.frame)}
                                        </span>
                                    }
                                />
                            ) : (
                                <ColorFinderFeedComment
                                    comment={item.data}
                                    onCommentClick={() => getMediaControls()?.setFrame(item.frame)}
                                    onReplyClick={onCommentReplyClick}
                                    onDeleteClick={handleDelete}
                                    timestamp={
                                        <span
                                            className={classNames(
                                                statusToTextColorClass(item.data.context.status)
                                            )}
                                        >
                                            {getTimestamp(item.frame)}
                                        </span>
                                    }
                                />
                            )}
                        </div>
                    ))}
                </div>
            </ScrollableArea>
            {!isScrollSynced && (
                <ButtonTertiary
                    className="absolute bottom-10 left-1/2 -translate-x-1/2 bg-black-700"
                    LeftIcon={Icons.RestartAlt}
                    title="Sync Scroll Position"
                    onClick={() => setIsScrollSynced(true)}
                />
            )}
        </div>
    )
}

interface ColorFinderEditProps {
    comment: Comment
    onFinished: () => void
}

const ColorFinderCommentEdit: FunctionComponent<ColorFinderEditProps> = ({
    comment,
    onFinished,
}) => {
    const inputRef = useRef<HTMLTextAreaElement>(null)
    const [input, setInput] = useState(comment.comment)
    const { updateComment, isMutating } = useUpdateComment()

    const formRef = useRef<HTMLFormElement>(null)

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault()
        updateComment({ commentId: comment.id, comment: input }).then(() => onFinished())
    }

    useEffect(() => {
        const textArea = inputRef.current
        if (!textArea) return

        textArea.style.height = "0px"

        const nextHeight = textArea.scrollHeight
        textArea.style.height = `${nextHeight}px`
    }, [input])

    useEffect(() => {
        formRef.current?.scrollIntoView()
    }, [])

    useEffect(() => {
        const textArea = inputRef.current
        if (!textArea) return

        textArea.style.height = "0px"

        const nextHeight = textArea.scrollHeight
        textArea.style.height = `${nextHeight}px`
    }, [input])

    return (
        <form
            ref={formRef}
            onSubmit={handleSubmit}
            className="flex flex-col w-full bg-white-100 rounded-lg divide-y divide-white-200 border border-white-200"
        >
            <ScrollableArea
                className={classNames("h-full min-h-0 max-h-52")}
                ref={(ref) => {
                    if (ref && hasScrollbar(ref satisfies HTMLElement).vertical) {
                        ref?.classList.add("mt-0.5")
                    } else {
                        ref?.classList.remove("mt-0.5")
                    }
                }}
                shouldPreventScrollbarShifting
            >
                <textarea
                    ref={inputRef}
                    autoComplete="off"
                    disabled={isMutating}
                    value={input}
                    onChange={(e) => {
                        e.stopPropagation()
                        setInput(e.target.value)
                    }}
                    onKeyDown={(e) => {
                        e.stopPropagation()

                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault()
                            e.currentTarget.form?.requestSubmit()
                        }
                    }}
                    className={classNames(
                        "focus:outline-none focus:ring-0 border-0 bg-transparent w-full",
                        "placeholder-gray-300 text-[14px] leading-relaxed",
                        "py-2.5 px-3 resize-none overflow-hidden",
                        "enabled:hover:placeholder-white enabled:text-white disabled:cursor-not-allowed"
                    )}
                    rows={1}
                    maxLength={5000}
                />
            </ScrollableArea>
            <div className="flex justify-end py-1.5 px-3">
                <div className="flex items-center gap-3">
                    <ButtonTertiary title="Cancel" onClick={onFinished} />
                    <IconButton
                        type="submit"
                        className={classNames(
                            "py-2 px-3 rounded-lg",
                            !!input && "bg-blue-500 text-gray-900"
                        )}
                        disabled={!input || isMutating}
                    >
                        <Icons.SmallIcon Icon={Icons.Send} />
                    </IconButton>
                </div>
            </div>
        </form>
    )
}

interface ColorFinderCommentInputProps {
    flight: VideoColorFinderFlight | ImageColorFinderFlight
    getMediaControls: GetMediaControls
    targetComment: Comment<"cf-frame"> | undefined
    disabled?: boolean
}

const ColorFinderCommentInput: FunctionComponent<ColorFinderCommentInputProps> = ({
    flight,
    getMediaControls,
    targetComment,
}) => {
    const { createComment, isMutating } = useCreateComment()
    const [status, setStatus] = useState<(typeof commentOptions)[number]>(commentOptions[0])
    const [input, setInput] = useState("")
    const inputRef = useRef<HTMLTextAreaElement>(null)

    const isLocal = isLocalColorFinderFlight(flight)

    const handleSendClick: FormEventHandler<HTMLFormElement> = async (e) => {
        const controls = getMediaControls()
        e.preventDefault()
        if (!controls) return

        const formElement = e.currentTarget
        const form = new FormData(e.currentTarget)

        const commentContext: CommentContextMap[CommentTarget] = targetComment
            ? { target: "cf-comment" }
            : { frame: controls?.getFrame(), status: status.status, target: "cf-frame" }

        await createComment({
            comment: (form.get("comment-input") as string) ?? "",
            context: commentContext,
            entityId: targetComment?.id ?? flight.id,
        })

        formElement.reset()

        setInput("")
    }

    useEffect(() => {
        const textArea = inputRef.current
        if (!textArea) return

        textArea.style.height = "0px"

        const nextHeight = textArea.scrollHeight
        textArea.style.height = `${nextHeight}px`
    }, [input])

    const handleInput: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
        e.stopPropagation()
        setInput(e.target.value)
    }

    if (isLocal)
        return (
            <div
                className={classNames(
                    "flex items-center justify-center bg-white-100 border border-white-200 rounded-lg text-white-600 mx-auto w-full h-[92px] z-10"
                )}
            >
                Upload to enable commenting
            </div>
        )

    return (
        <form
            onSubmit={handleSendClick}
            className={classNames(
                "flex flex-col relative w-full bg-white-100 rounded-lg overflow-hidden divide-y divide-white-200 border border-white-200 group/form",
                isLocal && "cursor-not-allowed"
            )}
        >
            <ScrollableArea
                className={classNames("h-full min-h-0 max-h-52")}
                ref={(ref) => {
                    if (ref && hasScrollbar(ref satisfies HTMLElement).vertical) {
                        ref?.classList.add("mt-0.5")
                    } else {
                        ref?.classList.remove("mt-0.5")
                    }
                }}
                shouldPreventScrollbarShifting
            >
                <textarea
                    ref={inputRef}
                    autoComplete="off"
                    value={input}
                    key={`comment-input-${!!targetComment}`}
                    autoFocus={!!targetComment}
                    onChange={handleInput}
                    onKeyDown={(e) => {
                        e.stopPropagation()

                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault()
                            e.currentTarget.form?.requestSubmit()
                        }
                    }}
                    disabled={isMutating || isLocal}
                    placeholder={targetComment ? "Leave a reply..." : "Leave a comment..."}
                    name="comment-input"
                    className={classNames(
                        "focus:outline-none focus:ring-0 border-0 bg-transparent w-full",
                        "placeholder-gray-300 text-[14px] leading-relaxed",
                        "py-2.5 px-3 resize-none overflow-hidden",
                        "enabled:hover:placeholder-white enabled:text-white disabled:cursor-not-allowed"
                    )}
                    rows={1}
                    maxLength={5000}
                />
            </ScrollableArea>
            <div className="flex justify-between py-1.5 px-3">
                <Dropdown>
                    <Dropdown.Trigger
                        className={classNames(
                            "flex gap-2 items-center text-white-400 enabled:hover:text-white disabled:cursor-not-allowed",
                            targetComment && "invisible"
                        )}
                        disabled={isLocal}
                    >
                        {statusToIcon(status.status)}
                        {status.title}
                        <Icons.SmallIcon Icon={Icons.ArrowDropdown} />
                    </Dropdown.Trigger>
                    <Dropdown.Menu position="top-left" className="w-40">
                        {commentOptions.map((option) => (
                            <Dropdown.ButtonItem
                                key={option.status}
                                className="flex gap-1"
                                onClick={() => setStatus(option)}
                            >
                                <Icons.SmallIcon
                                    Icon={Icons.Checkmark}
                                    className={classNames(
                                        "mr-2 text-green-500",
                                        option.status !== status.status && "opacity-0"
                                    )}
                                />
                                {statusToIcon(option.status)}
                                {option.title}
                            </Dropdown.ButtonItem>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
                <div className="flex items-center gap-3">
                    <IconButton
                        type="submit"
                        className="py-2 px-3 rounded-lg disabled:cursor-not-allowed enabled:bg-blue-500 enabled:text-gray-900"
                        disabled={!input || isLocal}
                    >
                        <Icons.SmallIcon Icon={Icons.Send} />
                    </IconButton>
                </div>
            </div>
        </form>
    )
}

interface ColorFinderIconProps {
    icon: Icon
    timestamp: ReactNode
    onIconClick: () => void
}

const ColorFinderFeedIcon: FunctionComponent<ColorFinderIconProps> = ({
    icon,
    timestamp,
    onIconClick,
}) => {
    const dispatch = useAppDispatch()

    const handleCoordinateClick: MouseEventHandler = async (e) => {
        e.stopPropagation()

        await navigator.clipboard.writeText(`${icon.latitude}, ${icon.longitude}`)
        dispatch(
            setGlobalNotificationContext({
                message: "Copied coordinates.",
                status: GlobalNotificationStatus.success,
                timeoutInMs: 3000,
            })
        )
    }

    return (
        <div
            className="flex items-start gap-2.5 min-h-min p-3 relative hover:bg-white-100"
            onClick={onIconClick}
        >
            <div className="h-5 flex items-center">
                <small className="mono text-gray-100 px-[5px]">{timestamp}</small>
            </div>

            <div className="h-5 flex items-center">
                <svg
                    data-src={getIconUrl(icon.url)}
                    style={{
                        color: icon.color
                            ? decodeNumberToColor(icon.color) || defaultIconColor
                            : undefined,
                        fill: icon.color
                            ? decodeNumberToColor(icon.color) || defaultIconColor
                            : undefined,
                    }}
                    height="1.5rem"
                    width="1.5rem"
                />
            </div>

            <div className="flex flex-col items-start gap-1">
                <h5 className="text-gray-50 leading-5">{icon.name} Placed</h5>
                <small
                    onClick={handleCoordinateClick}
                    className="text-gray-300 cursor-pointer hover:underline"
                >
                    {icon.latitude.toFixed(6)}, {icon.longitude.toFixed(6)}
                </small>
            </div>
        </div>
    )
}

const ColorFinderFeedComment = ({
    comment,
    timestamp,
    onReplyClick,
    onCommentClick,
}: {
    comment: Comment<"cf-frame">
    timestamp: ReactNode
    onReplyClick?: (comment: Comment<"cf-frame">) => void
    onDeleteClick: (comment: Comment<"cf-frame">) => void
    onCommentClick: (comment: Comment<"cf-frame">) => void
}) => {
    const { fullName } = useFullnameForUserId(comment.createdBy)

    const handleRepliesClick: MouseEventHandler = (e) => {
        e.stopPropagation()
        onReplyClick?.(comment)
    }

    return (
        <div
            className="group/comment flex items-start gap-2.5 p-3 relative hover:bg-white-100"
            onClick={() => onCommentClick(comment)}
        >
            <div className="flex items-center h-6">
                <small className="mono text-gray-100 px-[5px] min-w-[32px]">{timestamp}</small>
            </div>

            <div className="flex items-center h-6">
                {comment.context.status === "default" ? (
                    <div className="w-6 min-w-[24px] aspect-square rounded-lg overflow-hidden">
                        <ProfilePhoto userId={comment?.createdBy || ""} fullName={fullName || ""} />
                    </div>
                ) : (
                    statusToIcon(comment.context.status)
                )}
            </div>

            <div className="flex flex-col items-start justify-center min-h-[1.5rem] gap-1 flex-1 min-w-0">
                <h5 className="text-gray-50 break-words w-full">
                    {comment.comment.split("\n").map((line, index) => (
                        <Fragment key={index}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </h5>
                {comment.numReplies > 0 && (
                    <IconButton className="hover:text-blue-500" onClick={handleRepliesClick}>
                        <small>{comment.numReplies} Reply</small>
                    </IconButton>
                )}
            </div>

            <IconButton
                className={classNames(
                    "absolute right-3 h-6 bg-gray-700 invisible group-hover/comment:visible border-t border-transparent hover:bg-gray-500 hover:border-white-200 hover:shadow-black-900 hover:shadow rounded-md p-0.5"
                )}
                onClick={handleRepliesClick}
            >
                <Icons.SmallIcon Icon={Icons.Reply} />
            </IconButton>
        </div>
    )
}

const ColorFinderCommentRepliesFeed = ({
    flight,
    comment,
    onBackClick,
    getMediaControls,
    tiler,
}: {
    flight: VideoColorFinderFlight | ImageColorFinderFlight
    comment: Comment<"cf-frame">
    getMediaControls: GetMediaControls
    onBackClick: () => void
    tiler: ColorFinderTiler
}) => {
    const dispatch = useAppDispatch()

    const [editingCommentId, setEditingCommentId] = useState<string>()

    const { comments: replies = [] } = useComments({ entityId: comment.id })
    const { updateComment } = useUpdateComment()

    const timestampRef = useRef<HTMLHeadingElement>(null)

    const controls = getMediaControls()

    const handleDeleteComment = (comment: Comment) => {
        dispatch(
            showConfirmationModal({
                entityId: comment.id,
                type: WorkspaceConfirmationModalName.DeleteColorFinderComment,
            })
        )
        dispatch(showGlobalModal())
    }

    const handleEditComment = (comment: Comment) => {
        setEditingCommentId(comment.id)
    }

    const handleStatusChange = (option: (typeof commentOptions)[number]) => {
        updateComment({
            commentId: comment.id,
            context: { ...comment.context, status: option.status },
        })
    }

    let time
    if (isVideoFlight(flight)) {
        const numFrames = tiler.getNumFrames()
        const value = controls
            ? (comment.context.frame / numFrames) * controls.getVideoDuration()
            : 0
        time = formatTimestamp(value)
    } else {
        time = comment.context.frame + 1
    }

    const status =
        commentOptions.find((o) => o.status === comment.context.status) ?? commentOptions[0]
    return (
        <div className="flex flex-col gap-2 h-full">
            <div className="flex flex-col gap-3 px-3 pt-3">
                <div className="flex justify-between">
                    <IconButton className="flex items-center gap-1" onClick={onBackClick}>
                        <Icons.SmallIcon Icon={Icons.ArrowLeft} />
                        <small>Back</small>
                    </IconButton>
                    {time !== undefined && (
                        <h5
                            className="mono text-gray-100 bg-white-100 px-2 rounded-full h-fit"
                            ref={timestampRef}
                        >
                            {time}
                        </h5>
                    )}
                </div>
                <div className="flex gap-2 items-center">
                    <Dropdown>
                        <Dropdown.TriggerButton className="flex items-center flex-1 w-full !text-gray-100 border-gray-500">
                            {statusToIcon(status.status)}
                            {status.title}
                        </Dropdown.TriggerButton>
                        <Dropdown.Menu position="bottom-left" className="w-40">
                            {commentOptions.map((option) => (
                                <Dropdown.ButtonItem
                                    key={option.status}
                                    className="flex gap-1"
                                    onClick={() => handleStatusChange(option)}
                                >
                                    <Icons.SmallIcon
                                        Icon={Icons.Checkmark}
                                        className={classNames(
                                            "mr-2 text-green-500",
                                            option.status !== status.status && "opacity-0"
                                        )}
                                    />
                                    {statusToIcon(option.status)}
                                    {option.title}
                                </Dropdown.ButtonItem>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                    <Dropdown>
                        <Dropdown.Trigger>
                            <IconButton className="border border-gray-500 rounded-lg px-3 py-1.5">
                                <Icons.SmallIcon Icon={Icons.MoreVert} />
                            </IconButton>
                        </Dropdown.Trigger>
                        <Dropdown.Menu position="bottom-right">
                            <ReplyOptionsMenu
                                onDeleteClick={() => handleDeleteComment(comment)}
                                onEditClick={() => handleEditComment(comment)}
                            />
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>
            <ScrollableArea shouldPreventScrollbarShifting className="relative h-full">
                <div className="absolute flex flex-col w-full ">
                    {comment.id === editingCommentId ? (
                        <div className="p-3">
                            <ColorFinderCommentEdit
                                comment={comment}
                                onFinished={() => setEditingCommentId(undefined)}
                            />
                        </div>
                    ) : (
                        <RepliesFeedComment key={comment.id} comment={comment} hideOptions />
                    )}
                    {!!comment.numReplies && (
                        <div className="flex items-center gap-3 px-3">
                            <small>{comment.numReplies} Reply</small>
                            <Divider className="flex-1" />
                        </div>
                    )}
                    {replies.map((reply) => (
                        <div key={reply.id} className="w-full">
                            {reply.id === editingCommentId ? (
                                <div className="p-3">
                                    <ColorFinderCommentEdit
                                        comment={reply}
                                        onFinished={() => setEditingCommentId(undefined)}
                                    />
                                </div>
                            ) : (
                                <RepliesFeedComment
                                    onDelete={() => handleDeleteComment(reply)}
                                    onEdit={() => handleEditComment(reply)}
                                    comment={reply}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </ScrollableArea>
        </div>
    )
}

const RepliesFeedComment = ({
    comment,
    onDelete = () => {},
    onEdit = () => {},
    hideOptions = false,
}: {
    comment: Comment
    onDelete?: () => void
    onEdit?: () => void
    hideOptions?: boolean
}) => {
    const { fullName } = useFullnameForUserId(comment.createdBy)

    return (
        <div className="group/reply relative">
            <div className="flex items-start gap-2 hover:bg-white-100 p-3 ">
                <div className="w-9 min-w-[36px] aspect-square rounded-lg overflow-hidden">
                    <ProfilePhoto userId={comment?.createdBy || ""} fullName={fullName || ""} />
                </div>
                <div className="flex flex-col min-w-0 flex-1">
                    <div className="flex items-end gap-2">
                        <h5 className="text-white">{fullName}</h5>
                        <small>{getCommentDate(comment.createdAt)}</small>
                    </div>
                    <div className="flex flex-col">
                        <h5 className="text-gray-50 break-words w-full">
                            {comment.comment.split("\n").map((line, index) => (
                                <Fragment key={index}>
                                    {line}
                                    <br />
                                </Fragment>
                            ))}
                        </h5>
                        {comment.update && <small className="text-gray-300">Edited</small>}
                    </div>
                </div>
            </div>
            {!hideOptions && (
                <Dropdown>
                    <Dropdown.Trigger className="absolute right-3 top-2">
                        <IconButton
                            className={classNames(
                                "text-gray-100",
                                "bg-gray-700 invisible group-hover/reply:visible border-t border-transparent hover:bg-gray-500 hover:border-white-200 hover:shadow-black-900 hover:shadow rounded-md p-0.5"
                            )}
                        >
                            <Icons.SmallIcon Icon={Icons.MoreVert} />
                        </IconButton>
                    </Dropdown.Trigger>
                    <Dropdown.Menu position="bottom-right">
                        <ReplyOptionsMenu onDeleteClick={onDelete} onEditClick={onEdit} />
                    </Dropdown.Menu>
                </Dropdown>
            )}
        </div>
    )
}

const ReplyOptionsMenu = ({
    onDeleteClick,
    onEditClick,
}: {
    onEditClick: () => void
    onDeleteClick: () => void
}) => {
    return (
        <div>
            <Dropdown.ButtonItem
                className="flex gap-3 items-center px-3 py-1"
                onClick={onEditClick}
            >
                <Icons.SmallIcon Icon={Icons.Edit} className="text-gray-100" />
                Edit
            </Dropdown.ButtonItem>
            <Divider className="bg-white-200" />
            <Dropdown.ButtonItem
                className="flex gap-3 items-center px-3 py-1 !text-red-500"
                onClick={onDeleteClick}
            >
                <Icons.SmallIcon Icon={Icons.Trash} />
                Delete
            </Dropdown.ButtonItem>
        </div>
    )
}

const formatTimestamp = (timeSecondsFloat: number) => {
    const mins = Math.floor(timeSecondsFloat / 60).toString()
    const secs = Math.floor(timeSecondsFloat % 60).toString()
    return `${mins.padStart(2, "0")}:${secs.padStart(2, "0")}`
}

const getCommentDate = (unixDate: number) => {
    const date = new Date(unixDate)
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    // 1. Less than 1 minute
    if (diffInSeconds < 60) {
        return "now"
    }

    // 2. Less than 1 hour (Minute count)
    const diffInMinutes = Math.floor(diffInSeconds / 60)
    if (diffInMinutes < 60) {
        return `${diffInMinutes} ${diffInMinutes === 1 ? "min" : "mins"} ago`
    }

    // 3. Less than 24 hours (Time only)
    // "12:24pm"
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) {
        return date
            .toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
            })
            .toLowerCase()
            .replace(" ", "")
    }

    // 4. Older than 24 hours (Full Date)
    // "November 18, 2025"
    return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    })
}

const commentOptions: { status: CFCommentStatus; title: string }[] = [
    { status: "default", title: "Comment" },
    { status: "beenhere", title: "Cleared" },
    { status: "not_listed_location", title: "Unknown" },
    { status: "brightness_alert", title: "Investigate" },
    { status: "dangerous", title: "False" },
]

const statusToIcon = (status: CFCommentStatus) => {
    switch (status) {
        case "default":
            return <CommentIcon />
        case "beenhere":
            return <BeenHereIcon />
        case "brightness_alert":
            return <BrightnessAlertIcon />
        case "dangerous":
            return <DangerousIcon />
        case "not_listed_location":
            return <NotListedIcon />
        default:
            return <></>
    }
}

const statusToTextColorClass = (status: CFCommentStatus) => {
    switch (status) {
        case "default":
            return "text-gray-100"
        case "beenhere":
            return "text-green-200"
        case "brightness_alert":
            return "text-red-200"
        case "dangerous":
            return "text-blue-200"
        case "not_listed_location":
            return "text-orange-200"
        default:
            return ""
    }
}

export const findLatestComment = (
    comments: Comment<"cf-frame">[],
    currentFrame: number
): [Comment<"cf-frame"> | null, number] => {
    let latestComment: Comment<"cf-frame"> | null = null
    let maxFrame = -1

    for (let i = 0; i < comments.length; i++) {
        const comment = comments[i]
        const frame = comment.context.frame

        if (frame <= currentFrame && frame > maxFrame) {
            maxFrame = frame
            latestComment = comment
        }
    }

    return [latestComment, maxFrame]
}

export const findLatestIcon = (icons: Icon[], currentFrame: number): [Icon | null, number] => {
    let latestIcon: Icon | null = null
    let maxFrame = -1

    for (let i = 0; i < icons.length; i++) {
        const icon = icons[i]
        const payload = parseIconTargetPayload(icon.targetPayload)
        const frame = payload?.frame ?? -1

        if (frame > -1 && frame <= currentFrame && frame > maxFrame) {
            maxFrame = frame
            latestIcon = icon
        }
    }

    return [latestIcon, maxFrame]
}

export { ColorFinderFeed, ColorFinderCommentInput, statusToIcon, ColorFinderCommentRepliesFeed }
